import { create } from "zustand";
import { applyTheme, getInitialTheme, type ThemeId } from "../themes/themes";
import {
  LAYOUT_PRESETS,
  clampRightPane,
  clampSidebar,
  loadLayout,
  saveLayout,
  type LayoutPreset,
  type LayoutShape,
} from "./layout";
import type { TriageStatus } from "./types";

interface UIState {
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;

  selectedProjectId: string | null;
  setSelectedProjectId: (id: string | null) => void;

  selectedTaskId: string | null;
  setSelectedTaskId: (id: string | null) => void;

  rightPaneMode: "activity" | "chat" | "loops";
  setRightPaneMode: (mode: "activity" | "chat" | "loops") => void;

  // ----- Layout -----
  layout: LayoutShape;
  setSidebarWidth: (w: number) => void;
  setRightPaneWidth: (w: number) => void;
  toggleSidebar: () => void; // rail vs panel
  toggleRightPaneVisible: () => void;
  setRightPaneVisible: (visible: boolean) => void;
  applyLayoutPreset: (preset: LayoutPreset) => void;
  resetLayout: () => void;
  layoutPreset: LayoutPreset | null;

  rightPaneOpenMobile: boolean;
  setRightPaneOpenMobile: (open: boolean) => void;

  commandOpen: boolean;
  setCommandOpen: (open: boolean) => void;

  mainView: "board" | "tracks" | "security";
  setMainView: (view: "board" | "tracks" | "security") => void;

  // Left sidebar mode — Projects list vs the focused project's tasks-by-status.
  leftPanelView: "projects" | "tasks";
  setLeftPanelView: (view: "projects" | "tasks") => void;

  selectedTrackId: string | null;
  setSelectedTrackId: (id: string | null) => void;

  activeChatThreadId: string | null;
  setActiveChatThreadId: (id: string | null) => void;

  chatModel: string;
  setChatModel: (model: string) => void;

  showArchivedProjects: boolean;
  setShowArchivedProjects: (v: boolean) => void;

  // Board filter: show only tasks in a given triage state (null = no filter).
  // null means "show all"; set from the needs:human chip or the Loops counts.
  triageFilter: TriageStatus | null;
  setTriageFilter: (v: TriageStatus | null) => void;

  // ----- Batch confirm-done selection (Phase 2) -----
  // Task ids the operator has ticked to "Confirm together". Only In-Review
  // cards are selectable; cleared on project switch or after a batch confirm.
  batchSelection: string[];
  toggleBatchSelection: (id: string) => void;
  clearBatchSelection: () => void;

  // ----- Bulk add-to-track selection -----
  // Un-started (backlog/todo, untracked) task ids ticked to add to a track in
  // one shot. Cleared on project switch or after the bulk attach.
  trackSelection: string[];
  toggleTrackSelection: (id: string) => void;
  clearTrackSelection: () => void;
}

const initialTheme = getInitialTheme();
const initialLayout = loadLayout();
const initialChatModel = loadChatModel();
if (typeof window !== "undefined") {
  applyTheme(initialTheme);
}

function persist(layout: LayoutShape) {
  saveLayout(layout);
}

function loadChatModel(): string {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem("maestro.chat.model") || "";
}

function saveChatModel(model: string) {
  if (typeof window === "undefined") return;
  if (model) window.localStorage.setItem("maestro.chat.model", model);
  else window.localStorage.removeItem("maestro.chat.model");
}

export const useUI = create<UIState>((set, get) => ({
  theme: initialTheme,
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },

  selectedProjectId: null,
  setSelectedProjectId: (id) =>
    set({ selectedProjectId: id, batchSelection: [], trackSelection: [] }),

  selectedTaskId: null,
  setSelectedTaskId: (id) => set({ selectedTaskId: id }),

  rightPaneMode: "activity",
  setRightPaneMode: (mode) => set({ rightPaneMode: mode }),

  layout: initialLayout,
  layoutPreset: initialLayout.preset,
  setSidebarWidth: (w) => {
    const layout: LayoutShape = {
      ...get().layout,
      sidebarWidth: clampSidebar(w),
      preset: null,
    };
    persist(layout);
    set({ layout, layoutPreset: null });
  },
  setRightPaneWidth: (w) => {
    const layout: LayoutShape = {
      ...get().layout,
      rightPaneWidth: clampRightPane(w),
      preset: null,
    };
    persist(layout);
    set({ layout, layoutPreset: null });
  },
  toggleSidebar: () => {
    const cur = get().layout;
    const layout: LayoutShape = {
      ...cur,
      sidebarCollapsed: !cur.sidebarCollapsed,
      preset: null,
    };
    persist(layout);
    set({ layout, layoutPreset: null });
  },
  toggleRightPaneVisible: () => {
    const cur = get().layout;
    const layout: LayoutShape = {
      ...cur,
      rightPaneVisible: !cur.rightPaneVisible,
      preset: null,
    };
    persist(layout);
    set({ layout, layoutPreset: null });
  },
  setRightPaneVisible: (visible) => {
    const cur = get().layout;
    if (cur.rightPaneVisible === visible) return;
    const layout: LayoutShape = {
      ...cur,
      rightPaneVisible: visible,
      preset: null,
    };
    persist(layout);
    set({ layout, layoutPreset: null });
  },
  applyLayoutPreset: (preset) => {
    const shape = LAYOUT_PRESETS[preset].shape;
    const layout: LayoutShape = { ...shape, preset };
    persist(layout);
    set({ layout, layoutPreset: preset });
  },
  resetLayout: () => {
    const shape = LAYOUT_PRESETS.default.shape;
    const layout: LayoutShape = { ...shape, preset: "default" };
    persist(layout);
    set({ layout, layoutPreset: "default" });
  },

  rightPaneOpenMobile: false,
  setRightPaneOpenMobile: (open) => set({ rightPaneOpenMobile: open }),

  commandOpen: false,
  setCommandOpen: (open) => set({ commandOpen: open }),

  mainView: "board",
  setMainView: (view) => set({ mainView: view }),

  leftPanelView: "projects",
  setLeftPanelView: (view) => set({ leftPanelView: view }),

  selectedTrackId: null,
  setSelectedTrackId: (id) => set({ selectedTrackId: id }),

  activeChatThreadId: null,
  setActiveChatThreadId: (id) => set({ activeChatThreadId: id }),

  chatModel: initialChatModel,
  setChatModel: (model) => {
    saveChatModel(model);
    set({ chatModel: model });
  },

  triageFilter: null,
  setTriageFilter: (v) => set({ triageFilter: v }),
  showArchivedProjects: false,
  setShowArchivedProjects: (v) => set({ showArchivedProjects: v }),

  batchSelection: [],
  toggleBatchSelection: (id) =>
    set((s) => ({
      batchSelection: s.batchSelection.includes(id)
        ? s.batchSelection.filter((x) => x !== id)
        : [...s.batchSelection, id],
    })),
  clearBatchSelection: () => set({ batchSelection: [] }),

  trackSelection: [],
  toggleTrackSelection: (id) =>
    set((s) => ({
      trackSelection: s.trackSelection.includes(id)
        ? s.trackSelection.filter((x) => x !== id)
        : [...s.trackSelection, id],
    })),
  clearTrackSelection: () => set({ trackSelection: [] }),
}));
